import React, { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ProductLine {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  itemCount: number;
  versions?: ProductLineVersion[];
  createdAt: string;
  updatedAt: string;
}

interface ProductLineVersion {
  id: string;
  productLineId: string;
  versionNumber: string;
  versionName: string | null;
  effectiveDate: string;
  endDate: string | null;
  status: 'active' | 'retired' | 'warranty_only';
  changeNotes: string | null;
  bomItemCount: number;
  createdAt: string;
  updatedAt: string;
}

// API helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

// Modal Component
function Modal({ isOpen, onClose, title, children, size = 'md' }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
}) {
  if (!isOpen) return null;

  const sizeClass = {
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }[size];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className={`relative bg-white rounded-xl shadow-2xl ${sizeClass} w-full max-h-[90vh] overflow-y-auto`}>
          <div className="border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Product Line Form
function ProductLineForm({
  productLine,
  onSave,
  onCancel,
}: {
  productLine: Partial<ProductLine> | null;
  onSave: (data: { name: string; code?: string; description?: string; sortOrder?: number; isActive: boolean }) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    name: productLine?.name || '',
    code: productLine?.code || '',
    description: productLine?.description || '',
    sortOrder: productLine?.sortOrder ?? 0,
    isActive: productLine?.isActive !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              name === 'sortOrder' ? Number(value) : value
    }));
  };

  // Auto-generate code from name
  const autoGenerateCode = () => {
    if (!form.code && form.name) {
      const code = form.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      setForm(prev => ({ ...prev, code }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Product line name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        description: form.description.trim() || undefined,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save product line');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Product Line Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          onBlur={autoGenerateCode}
          required
          placeholder="e.g., V-Track Standard"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Code
        </label>
        <input
          type="text"
          name="code"
          value={form.code}
          onChange={handleChange}
          placeholder="e.g., v_track (auto-generated if empty)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
        />
        <p className="text-xs text-gray-500 mt-1">
          Used for filtering in BOM. Lowercase letters, numbers, and underscores only.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={2}
          placeholder="Optional description of this product line..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort Order
          </label>
          <input
            type="number"
            name="sortOrder"
            value={form.sortOrder}
            onChange={handleChange}
            onWheel={(e) => e.currentTarget.blur()}
            min={0}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
        </div>

        <div className="flex items-center pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : productLine?.id ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}

// Version Form
function VersionForm({
  version,
  onSave,
  onCancel,
}: {
  version: Partial<ProductLineVersion> | null;
  onSave: (data: {
    versionNumber: string;
    versionName?: string;
    effectiveDate: string;
    endDate?: string;
    status: 'active' | 'retired' | 'warranty_only';
    changeNotes?: string;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    versionNumber: version?.versionNumber || '',
    versionName: version?.versionName || '',
    effectiveDate: version?.effectiveDate?.split('T')[0] || new Date().toISOString().split('T')[0],
    endDate: version?.endDate?.split('T')[0] || '',
    status: version?.status || 'active' as 'active' | 'retired' | 'warranty_only',
    changeNotes: version?.changeNotes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.versionNumber.trim()) {
      setError('Version number is required');
      return;
    }
    if (!form.effectiveDate) {
      setError('Effective date is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        versionNumber: form.versionNumber.trim(),
        versionName: form.versionName.trim() || undefined,
        effectiveDate: form.effectiveDate,
        endDate: form.endDate || undefined,
        status: form.status,
        changeNotes: form.changeNotes.trim() || undefined,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save version');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Version Number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="versionNumber"
            value={form.versionNumber}
            onChange={handleChange}
            required
            placeholder="e.g., 1.0, 1.1, 2.0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Version Name
          </label>
          <input
            type="text"
            name="versionName"
            value={form.versionName}
            onChange={handleChange}
            placeholder="e.g., Original Release"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Effective Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="effectiveDate"
            value={form.effectiveDate}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            name="endDate"
            value={form.endDate}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Leave blank if current</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="active">Active</option>
            <option value="retired">Retired</option>
            <option value="warranty_only">Warranty Only</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Change Notes
        </label>
        <textarea
          name="changeNotes"
          value={form.changeNotes}
          onChange={handleChange}
          rows={3}
          placeholder="What changed from the previous version..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : version?.id ? 'Update Version' : 'Add Version'}
        </button>
      </div>
    </form>
  );
}

// Version Status Badge
function VersionStatusBadge({ status }: { status: 'active' | 'retired' | 'warranty_only' }) {
  const config = {
    active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
    retired: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Retired' },
    warranty_only: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Warranty Only' },
  };

  const c = config[status];

  return (
    <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', c.bg, c.text)}>
      {c.label}
    </span>
  );
}

// Versions Panel - Shows within the expanded product line row
function VersionsPanel({
  productLine,
  onVersionsChange,
}: {
  productLine: ProductLine;
  onVersionsChange: () => void;
}) {
  const [versions, setVersions] = useState<ProductLineVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<ProductLineVersion | null>(null);
  const [snapshotting, setSnapshotting] = useState<string | null>(null);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/product-lines/${productLine.id}/versions`);
      setVersions(data || []);
    } catch (err) {
      console.error('Failed to load versions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, [productLine.id]);

  const handleSaveVersion = async (data: any) => {
    if (editingVersion?.id) {
      await apiRequest(`/product-lines/${productLine.id}/versions/${editingVersion.id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    } else {
      await apiRequest(`/product-lines/${productLine.id}/versions`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    }
    setModalOpen(false);
    setEditingVersion(null);
    loadVersions();
    onVersionsChange();
  };

  const handleDeleteVersion = async (version: ProductLineVersion) => {
    if (!window.confirm(`Are you sure you want to delete version ${version.versionNumber}? This cannot be undone.`)) {
      return;
    }
    try {
      await apiRequest(`/product-lines/${productLine.id}/versions/${version.id}`, {
        method: 'DELETE',
      });
      loadVersions();
      onVersionsChange();
    } catch (err: any) {
      alert(err.message || 'Failed to delete version');
    }
  };

  const handleCreateSnapshot = async (version: ProductLineVersion) => {
    if (!window.confirm(`This will lock the current BOM configuration for version ${version.versionNumber}. Continue?`)) {
      return;
    }
    setSnapshotting(version.id);
    try {
      await apiRequest(`/product-lines/${productLine.id}/versions/${version.id}/snapshot`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      loadVersions();
      alert(`BOM snapshot created for version ${version.versionNumber}`);
    } catch (err: any) {
      alert(err.message || 'Failed to create snapshot');
    } finally {
      setSnapshotting(null);
    }
  };

  const handleChangeStatus = async (version: ProductLineVersion, newStatus: 'active' | 'retired' | 'warranty_only') => {
    try {
      await apiRequest(`/product-lines/${productLine.id}/versions/${version.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      loadVersions();
    } catch (err: any) {
      alert(err.message || 'Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="py-4 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-t border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Versions</h3>
        <button
          onClick={() => {
            setEditingVersion(null);
            setModalOpen(true);
          }}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Version
        </button>
      </div>

      {versions.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm">
          No versions yet. Add a version to start tracking BOM changes.
        </div>
      ) : (
        <div className="space-y-2">
          {versions.map((version) => (
            <div
              key={version.id}
              className="bg-white rounded-lg border border-gray-200 p-3"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      V{version.versionNumber}
                    </span>
                    {version.versionName && (
                      <span className="text-gray-500">- {version.versionName}</span>
                    )}
                    <VersionStatusBadge status={version.status} />
                    {version.bomItemCount > 0 && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                        {version.bomItemCount} BOM items locked
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Effective: {new Date(version.effectiveDate).toLocaleDateString()}
                    {version.endDate && (
                      <> - End: {new Date(version.endDate).toLocaleDateString()}</>
                    )}
                  </div>
                  {version.changeNotes && (
                    <p className="text-sm text-gray-600 mt-2 italic">
                      {version.changeNotes}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Status dropdown */}
                  <select
                    value={version.status}
                    onChange={(e) => handleChangeStatus(version, e.target.value as any)}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="active">Active</option>
                    <option value="retired">Retired</option>
                    <option value="warranty_only">Warranty Only</option>
                  </select>

                  {/* Lock BOM button */}
                  <button
                    onClick={() => handleCreateSnapshot(version)}
                    disabled={snapshotting === version.id}
                    className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
                    title="Lock current BOM configuration for this version"
                  >
                    {snapshotting === version.id ? 'Locking...' : 'Lock BOM'}
                  </button>

                  {/* Edit button */}
                  <button
                    onClick={() => {
                      setEditingVersion(version);
                      setModalOpen(true);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edit
                  </button>

                  {/* Delete button */}
                  <button
                    onClick={() => handleDeleteVersion(version)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Version Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingVersion(null);
        }}
        title={editingVersion ? `Edit Version ${editingVersion.versionNumber}` : 'Add Version'}
      >
        <VersionForm
          version={editingVersion}
          onSave={handleSaveVersion}
          onCancel={() => {
            setModalOpen(false);
            setEditingVersion(null);
          }}
        />
      </Modal>
    </div>
  );
}

// Product Lines Page
export function ProductLinesPage() {
  const [productLines, setProductLines] = useState<ProductLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProductLine, setEditingProductLine] = useState<ProductLine | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [expandedProductLine, setExpandedProductLine] = useState<string | null>(null);

  const loadProductLines = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/product-lines?includeInactive=${showInactive}`);
      setProductLines(data || []);
    } catch (err) {
      console.error('Failed to load product lines:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProductLines();
  }, [showInactive]);

  const handleSaveProductLine = async (data: { name: string; code?: string; description?: string; sortOrder?: number; isActive: boolean }) => {
    if (editingProductLine?.id) {
      await apiRequest(`/product-lines/${editingProductLine.id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    } else {
      await apiRequest('/product-lines', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    }
    setModalOpen(false);
    setEditingProductLine(null);
    loadProductLines();
  };

  const handleDeleteProductLine = async (productLine: ProductLine) => {
    if (productLine.itemCount > 0) {
      alert(`Cannot delete "${productLine.name}" - ${productLine.itemCount} inventory items are using it. Remove from items first.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${productLine.name}"? This cannot be undone.`)) return;
    try {
      await apiRequest(`/product-lines/${productLine.id}`, { method: 'DELETE' });
      loadProductLines();
    } catch (err: any) {
      alert(err.message || 'Failed to delete product line');
    }
  };

  const handleToggleActive = async (productLine: ProductLine) => {
    try {
      await apiRequest(`/product-lines/${productLine.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !productLine.isActive }),
      });
      loadProductLines();
    } catch (err: any) {
      alert(err.message || 'Failed to update product line');
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedProductLine((prev) => (prev === id ? null : id));
  };

  return (
    <>
      <PageHeader
        title="Product Lines"
        subtitle="Manage product lines and their versions for BOM configuration"
        actions={
          <button
            onClick={() => {
              setEditingProductLine(null);
              setModalOpen(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Product Line
          </button>
        }
      />

      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Product Lines</h2>
            <p className="text-sm text-gray-500">
              Click on a product line to manage its versions. Each version locks a snapshot of BOM configuration.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Show inactive</span>
          </label>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : productLines.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No product lines found.</p>
            <p className="text-sm mt-1">Click "Add Product Line" to create one.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {productLines.map((pl) => (
              <div key={pl.id} className={clsx('border rounded-lg overflow-hidden', !pl.isActive && 'opacity-60')}>
                {/* Product Line Row */}
                <div
                  className={clsx(
                    'flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50',
                    expandedProductLine === pl.id && 'bg-gray-50'
                  )}
                  onClick={() => toggleExpand(pl.id)}
                >
                  <div className="flex items-center gap-4">
                    <button className="text-gray-400">
                      <svg
                        className={clsx('w-5 h-5 transition-transform', expandedProductLine === pl.id && 'rotate-90')}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>

                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{pl.name}</span>
                        <code className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">{pl.code}</code>
                        {!pl.isActive && (
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs">Inactive</span>
                        )}
                      </div>
                      {pl.description && (
                        <p className="text-sm text-gray-500 mt-0.5">{pl.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                    <span className={clsx(
                      'px-2 py-1 rounded-full text-xs font-medium',
                      pl.itemCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                    )}>
                      {pl.itemCount} items
                    </span>

                    <button
                      onClick={() => handleToggleActive(pl)}
                      className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium transition-colors',
                        pl.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      {pl.isActive ? 'Active' : 'Inactive'}
                    </button>

                    <button
                      onClick={() => {
                        setEditingProductLine(pl);
                        setModalOpen(true);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDeleteProductLine(pl)}
                      className={clsx(
                        'text-red-600 hover:text-red-800 text-sm',
                        pl.itemCount > 0 && 'opacity-50 cursor-not-allowed'
                      )}
                      title={pl.itemCount > 0 ? `${pl.itemCount} items use this product line` : 'Delete product line'}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Versions Panel (expandable) */}
                {expandedProductLine === pl.id && (
                  <VersionsPanel productLine={pl} onVersionsChange={loadProductLines} />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800">
              <p><strong>Version Control:</strong> Each product line can have multiple versions (e.g., V1.0, V1.1, V2.0).</p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li><strong>Active:</strong> Used for new orders</li>
                <li><strong>Retired:</strong> No longer available for new orders</li>
                <li><strong>Warranty Only:</strong> Only for warranty/remake orders</li>
                <li><strong>Lock BOM:</strong> Captures current BOM configuration for this version</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Product Line Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingProductLine(null);
        }}
        title={editingProductLine ? 'Edit Product Line' : 'Create Product Line'}
      >
        <ProductLineForm
          productLine={editingProductLine}
          onSave={handleSaveProductLine}
          onCancel={() => {
            setModalOpen(false);
            setEditingProductLine(null);
          }}
        />
      </Modal>
    </>
  );
}

export default ProductLinesPage;
